// src/Pages/Profile/AdminProfile.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
} from "@mui/material";
import AppSnackbar from "../Components/Snackbar/snackbar";
import apiEndpoints from "../apiconfig";

const API_URL = apiEndpoints.admin_profile; // CHANGE THIS

const AdminProfile = () => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // LOAD DATA FROM BACKEND
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setSnack({
        open: true,
        message: "Session expired! Please login again.",
        severity: "error",
      });
      return;
    }

    fetch(`${API_URL}?action=get`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.status) {
          setForm(res.data);
          localStorage.setItem("user", JSON.stringify(res.data));
        } else {
          setSnack({ open: true, message: res.message, severity: "error" });
        }
      })
      .catch(() =>
        setSnack({
          open: true,
          message: "Network error! Unable to fetch profile",
          severity: "error",
        })
      );
  }, []);

  const handleChange = (key, value) => {
    if (["mobile", "phone", "pincode"].includes(key)) {
      if (!/^\d*$/.test(value)) return;
    }

    if (key === "gstIn") value = value.toUpperCase();

    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    const newErrors = {};

    const required = [
      "organization_name",
      "email",
      "mobile",
      "address",
      "phone",
      "country",
      "state",
      "city",
      "pincode",
    ];

    required.forEach((field) => {
      if (!form[field] || form[field].trim() === "")
        newErrors[field] = `${field} is required`;
    });

    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      newErrors.email = "Enter a valid email address";

    if (form.mobile && !/^[0-9]{10}$/.test(form.mobile))
      newErrors.mobile = "Mobile number must be 10 digits";

    if (form.phone && !/^[0-9]{10,}$/.test(form.phone))
      newErrors.phone = "Phone must be at least 10 digits";

    if (form.gstIn && form.gstIn.trim() !== "" && !/^[0-9A-Z]{15}$/.test(form.gstIn))
      newErrors.gstIn = "Enter valid 15-character GST number";

    if (form.pincode && !/^[0-9]{6}$/.test(form.pincode))
      newErrors.pincode = "Pincode must be 6 digits";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return setSnack({
        open: true,
        message: "Please correct the highlighted fields",
        severity: "error",
      });
    }

    // ---- SEND UPDATE TO BACKEND ----
    const res = await fetch(`${API_URL}?action=update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`, // FIXED
      },
      body: JSON.stringify(form),
    })
      .then((r) => r.json())
      .catch(() => ({
        status: false,
        message: "Network error! Please try again",
      }));

    if (res.status) {
      localStorage.setItem("user", JSON.stringify(form));
      setSnack({ open: true, message: res.message, severity: "success" });
    } else {
      setSnack({ open: true, message: res.message, severity: "error" });
    }
  };

  return (
    <Box
      sx={{
        p: 4,
        borderRadius: 4,
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.15) 100%)",
        backdropFilter: "blur(16px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        mx: "auto",
      }}
    >
      {/* TITLE */}
      <Box
        sx={{
          mb: 3,
          pb: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Admin Profile
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Manage your organization details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* LOGO */}
        <Grid item xs={12} md={3} textAlign="center">
          <Avatar
            src={form.logo || undefined}
            sx={{
              width: 110,
              height: 110,
              mx: "auto",
              mb: 2,
              border: "3px solid #fff",
              boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
              ":hover": { transform: "scale(1.05)" },
            }}
          />
          <Button variant="outlined" component="label" fullWidth>
            Upload Logo
            <input
              type="file"
              hidden
              onChange={(e) =>
                handleChange("logo", URL.createObjectURL(e.target.files[0]))
              }
            />
          </Button>
        </Grid>

        {/* FORM */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2.5}>
            {[
              ["organization_name", "Organization Name *"],
              ["email", "Email *"],
              ["mobile", "Mobile *"],
              ["phone", "Phone"],
              ["gstIn", "GST Number"],
              ["country", "Country"],
              ["state", "State"],
              ["city", "City"],
              ["pincode", "Pincode"],
            ].map(([key, label]) => (
              <Grid
                item
                xs={12}
                md={
                  key === "state" || key === "city" || key === "pincode" ? 4 : 6
                }
                key={key}
              >
                <TextField
                  fullWidth
                  label={label}
                  value={form[key] || ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                  error={Boolean(errors[key])}
                  helperText={errors[key] || ""}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                minRows={3}
                value={form.address || ""}
                onChange={(e) => handleChange("address", e.target.value)}
                error={Boolean(errors.address)}
                helperText={errors.address && "Address is required"}
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" fullWidth onClick={handleSubmit}>
                Save Profile
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <AppSnackbar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack({ ...snack, open: false })}
      />
    </Box>
  );
};

export default AdminProfile;
